import {
  ArrowRightIcon,
  CopyIcon,
  KeyboardIcon,
  LogOutIcon,
  PanelLeftIcon,
  PlusIcon,
  RefreshCwIcon,
  Share2Icon,
  type LucideIcon,
} from "lucide-react"
import type { PersonView } from "@beacon/core/access"
import type { Project, SearchHit } from "@beacon/core/projects"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react"
import { useMatch, useNavigate } from "react-router"
import {
  canShare,
  colourSchemes,
  copyText,
  Disc,
  IdentityTile,
  Kbd,
  shareLink,
  StatusIcon,
  TypeIcon,
  useColourScheme,
  useViewport,
} from "@/design-system/index.ts"
import { installIcon, useInstallOffer } from "@/installed-app/install-offer.tsx"
import { reloadToUpdate, useUpdate } from "@/installed-app/updates.ts"
import { Button } from "@/primitives/button.tsx"
import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/primitives/combobox.tsx"
import { Dialog, DialogContent, DialogTitle } from "@/primitives/dialog.tsx"
import { useSidebar } from "@/primitives/sidebar.tsx"
import { cn } from "@/primitives/utils"
import { pathOfKey } from "@/projects/board.ts"
import { ticketSearchQuery } from "@/projects/client.ts"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { answers, ticketJumpOf } from "./describe.ts"
import { adminDestinations, destinations } from "./destinations.ts"
import {
  chooseIn,
  goToOf,
  groupsOf,
  peopleAnswering,
  ticketsPending,
  ticketsShown,
  ticketWordsOf,
  type PaletteGroup,
} from "./palette.ts"

/*
 * One box to go anywhere or do anything from: ⌘K, `/` on a page with no search of its own, or the
 * sidebar's search. It offers the pages the Member can reach, their Projects, a ticket by its key,
 * and the shell's actions, and narrows them as they type. Typed words also find people by name,
 * from the people directory the shell already holds, and tickets by their words, from the web API's
 * ticket search once the Member pauses (`palette.ts` decides what each group holds, in what order,
 * and when a choice closes the palette).
 *
 * It is a Combobox list held open inside a Dialog at the top of the viewport, so the list grows
 * downwards without the input moving. The palette filters for itself, so it knows when nothing is
 * left and can pin the ticket a typed key names above everything else.
 *
 * On the compact layout it is Search, opened from the tab bar: the whole screen, its field at the
 * top beside Cancel, its rows a finger's height, and no keys to hint at. On touch it leaves out
 * what only a keyboard does (the Shortcuts dialog, toggling the sidebar), and wherever the browser
 * can share it offers Share link beside Copy link. Wherever Beacon can be installed it offers Install
 * Beacon, and while a new release waits, Update Beacon.
 */

type PaletteContext = { openPalette: () => void }

/** Opens the command palette, for a page's own "Search" button (Not found has one). */
export const CommandPaletteContext = createContext<PaletteContext>({
  openPalette: () => undefined,
})

export const useCommandPalette = () => useContext(CommandPaletteContext)

type Entry = {
  id: string
  group: PaletteGroup
  label: string
  /** Faint text before the label: a ticket's key. */
  prefix?: string
  /** Faint text after the label: a Project's key, "Current" beside the scheme in use. */
  secondary?: string
  /** Faint text at the row's end, giving way to the label: the Project a ticket is in. */
  trailing?: string
  glyph: ReactNode
  shortcut?: ShortcutId
  /** What choosing it does; a navigation answers once the page it went to has arrived. */
  run: () => void | Promise<void>
}

/**
 * The first row is highlighted from the moment the palette opens, so ⌘K then ↵ goes Home, and
 * after every keystroke. Base UI's Combobox does this (`"always"`), but its public type admits only
 * `true`, which waits for the first keystroke, so the value is passed through as the type it hides.
 */
const highlightFirst = "always" as unknown as boolean

const icon = (Icon: LucideIcon) => (
  <Icon aria-hidden className="size-4 text-muted-foreground compact:size-4.5" />
)

/**
 * The on-screen keyboard's form for the palette's field on a phone or a tablet: a Search key, and
 * nothing capitalised, corrected or underlined in a key or a name. The desk's field keeps the
 * browser's own.
 */
const phoneKeyboard = {
  enterKeyHint: "search",
  autoCapitalize: "off",
  autoCorrect: "off",
  spellCheck: false,
} as const

/** How long the Member pauses in their typing before the palette asks for the tickets it finds. */
const searchAfter = 250

/** `value`, once it has stayed the same for `ms`. */
function useSettled<T>(value: T, ms: number) {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const waiting = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(waiting)
  }, [value, ms])
  return settled
}

/** A found ticket's circle: its Status's, or an Epic's mark, as an Epic has no Status. */
const hitGlyph = ({ status, type }: SearchHit) =>
  status ? <StatusIcon category={status.category} /> : <TypeIcon type={type} />

/** The groups whose rows are what the words found, so they aren't narrowed by them again. */
const alreadyMatched = new Set<PaletteGroup>(["Jump to ticket", "People", "Tickets"])

/**
 * The palette itself, open while `open`. `projects` are the Member's own (the sidebar's list),
 * `people` everyone the shell's people directory holds, and the rest is what the shell can do for
 * them.
 */
export function CommandPalette({
  open,
  onOpenChange,
  admin,
  projects,
  people,
  onShowShortcuts,
  onSignOut,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  admin: boolean
  projects: readonly Project[]
  people: readonly PersonView[]
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const [query, setQuery] = useState("")
  const popup = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { toggleSidebar } = useSidebar()
  const { scheme, setScheme } = useColourScheme()
  const { compact, touch } = useViewport()
  // The desk's palette stays as it was; a phone or a tablet shares where its browser can.
  const sharing = (compact || touch) && canShare()
  const onProject = useMatch("/projects/:key/*")?.params.key
  const current = projects.find(({ key }) => key === onProject)
  // C makes a new ticket on the Board alone, so the entry wears its key only there.
  const onBoard = useMatch("/projects/:key") !== null
  // The words worth a ticket search, once the Member pauses; what it found stays on screen while the
  // next search runs, and goes once the field holds nothing worth searching.
  const words = useSettled(ticketWordsOf(query), searchAfter)
  const found = useQuery({
    ...ticketSearchQuery(words ?? ""),
    enabled: open && words !== undefined,
    placeholderData: keepPreviousData,
  })
  const hits = ticketsShown(found.data, query)
  const searching = ticketsPending(query, words, found.isFetching)
  const projectNames = new Map(projects.map(({ key, name }) => [key, name]))
  const update = useUpdate().state.status !== "current"
  const installing = useInstallOffer()

  // ⌘K again, from inside the palette, puts it away.
  useShortcut("command-palette", () => close(), { within: popup, enabled: open })

  function close() {
    onOpenChange(false)
    setQuery("")
  }

  const jump = ticketJumpOf(query)
  const entries: Entry[] = [
    ...(jump
      ? [
          {
            id: "jump",
            group: "Jump to ticket",
            label: `Open ${jump.key}`,
            glyph: icon(ArrowRightIcon),
            run: () => navigate(jump.path),
          } as const,
        ]
      : []),
    ...goToOf([...destinations, ...(admin ? adminDestinations : [])], { compact, query }).map(
      ({ to, title, Icon, chord }): Entry => ({
        id: `go ${to}`,
        group: "Go to",
        label: title,
        glyph: icon(Icon),
        shortcut: chord,
        run: () => navigate(to),
      }),
    ),
    ...projects.map(({ key, name }): Entry => ({
      id: `project ${key}`,
      group: "Projects",
      label: name,
      secondary: key,
      glyph: <IdentityTile projectKey={key} size={16} />,
      run: () => navigate(`/projects/${key}`),
    })),
    ...peopleAnswering(query, people).map((person): Entry => ({
      id: `person ${person.id}`,
      group: "People",
      label: person.name,
      glyph: <Disc person={person} size={compact ? 20 : 16} />,
      run: () => navigate(`/people/${person.id}`),
    })),
    ...hits.flatMap((hit): Entry[] => {
      const path = pathOfKey(hit.key)
      return path
        ? [
            {
              id: `ticket ${hit.key}`,
              group: "Tickets",
              label: hit.title,
              prefix: hit.key,
              trailing: projectNames.get(hit.projectKey) ?? hit.projectKey,
              glyph: (
                <span className="flex size-4 shrink-0 items-center justify-center compact:size-4.5">
                  {hitGlyph(hit)}
                </span>
              ),
              run: () => navigate(path),
            },
          ]
        : []
    }),
    // A new ticket is made on its Board, so the palette takes the Member there with the composer
    // open; only where they may make one.
    ...(current && current.access === "write" && !current.archived
      ? [
          {
            id: "new ticket",
            group: "Actions",
            label: `New ticket in ${current.name}`,
            glyph: icon(PlusIcon),
            ...(onBoard && { shortcut: "new-ticket" }),
            run: () => navigate(`/projects/${current.key}?new=1`),
          } satisfies Entry,
        ]
      : []),
    {
      id: "new project",
      group: "Actions",
      label: "New Project",
      glyph: icon(PlusIcon),
      run: () => navigate("/projects?new=1"),
    },
    ...(admin
      ? [
          {
            id: "new group",
            group: "Actions",
            label: "New group",
            glyph: icon(PlusIcon),
            run: () => navigate("/groups?new=1"),
          } as const,
        ]
      : []),
    ...colourSchemes.map(({ value, label, Icon }): Entry => ({
      id: `theme ${value}`,
      group: "Actions",
      label: `Theme: ${label}`,
      ...(value === scheme && { secondary: "Current" }),
      glyph: icon(Icon),
      run: () => setScheme(value),
    })),
    ...(touch
      ? []
      : [
          {
            id: "toggle sidebar",
            group: "Actions",
            label: "Toggle sidebar",
            glyph: icon(PanelLeftIcon),
            shortcut: "toggle-sidebar",
            run: toggleSidebar,
          } as const,
        ]),
    {
      id: "copy link",
      group: "Actions",
      label: "Copy link",
      glyph: icon(CopyIcon),
      run: () => void copyText(window.location.href, "Link copied"),
    },
    ...(sharing
      ? [
          {
            id: "share link",
            group: "Actions",
            label: "Share link",
            glyph: icon(Share2Icon),
            run: () => shareLink(document.title, window.location.href),
          } as const,
        ]
      : []),
    ...(touch
      ? []
      : [
          {
            id: "shortcuts",
            group: "Actions",
            label: "Keyboard shortcuts",
            glyph: icon(KeyboardIcon),
            shortcut: "shortcuts",
            run: onShowShortcuts,
          } as const,
        ]),
    ...(update
      ? [
          {
            id: "update",
            group: "Actions",
            label: "Update Beacon",
            glyph: icon(RefreshCwIcon),
            run: reloadToUpdate,
          } as const,
        ]
      : []),
    ...(installing.offered
      ? [
          {
            id: "install",
            group: "Actions",
            label: "Install Beacon",
            glyph: icon(installIcon(compact)),
            run: installing.install,
          } as const,
        ]
      : []),
    {
      id: "sign out",
      group: "Actions",
      label: "Sign out",
      glyph: icon(LogOutIcon),
      run: onSignOut,
    },
  ]
  // A typed key is exactly one ticket, so its jump stands alone at the top, whatever else the
  // words happen to match; the people and tickets found are already what the words match. They
  // are listed group by group, the order the rows render in, so the Combobox's first item is the
  // first row it highlights.
  const groups = groupsOf(compact)
  const shown = groups.flatMap((group) =>
    entries.filter(
      (entry) =>
        entry.group === group &&
        (alreadyMatched.has(group) || answers(query, [entry.label, entry.secondary])),
    ),
  )

  function choose(id: string | null) {
    const entry = shown.find((candidate) => candidate.id === id)
    if (!entry) return
    void chooseIn(compact, entry.run, close)
  }

  const input = (
    <ComboboxInput
      ref={field}
      variant="search"
      placeholder="Search or jump to…"
      aria-label="Search or jump to"
      {...(compact || touch ? phoneKeyboard : {})}
      // The palette's input is its title: 15px on a 48px row, and 16px on touch, which iOS
      // doesn't zoom in on. On compact it is a filled field beside Cancel.
      className="h-12 px-2 [&_input]:text-title touch:[&_input]:text-field compact:h-10 compact:flex-1 compact:rounded-lg compact:border-0 compact:bg-muted compact:px-1"
    />
  )

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        ref={popup}
        position="top"
        size="lg"
        fullScreen
        // A sheet focuses itself on opening, which leaves Search waiting for a second tap on its
        // field; the full screen hands focus to the field, as the desk's dialog does by default.
        initialFocus={compact ? field : undefined}
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{compact ? "Search" : "Command palette"}</DialogTitle>
        <Combobox
          inline
          open
          autoHighlight={highlightFirst}
          items={shown.map(({ id }) => id)}
          filter={null}
          value={null}
          onValueChange={choose}
          inputValue={query}
          onInputValueChange={(next, { reason }) => {
            if (reason === "input-change") setQuery(next)
            else if (reason === "input-clear") setQuery("")
          }}
        >
          {compact ? (
            <div className="flex shrink-0 items-center gap-1 border-b border-border-subtle py-2 pr-2 pl-4">
              {input}
              <Button variant="ghost" onClick={close} className="px-2.5 font-medium">
                Cancel
              </Button>
            </div>
          ) : (
            input
          )}
          <ComboboxList className="max-h-96 outline-none data-empty:p-1 compact:max-h-none compact:min-h-0 compact:flex-1 compact:px-0 compact:pb-2 compact:data-empty:flex-none">
            {groups.map((group) => {
              const inGroup = shown.filter((entry) => entry.group === group)
              if (inGroup.length === 0) return null
              return (
                <ComboboxGroup key={group}>
                  <ComboboxLabel className="compact:px-4 compact:pt-3">{group}</ComboboxLabel>
                  {inGroup.map((entry) => (
                    <ComboboxItem
                      key={entry.id}
                      value={entry.id}
                      className={cn(
                        "h-9 gap-2.5 px-3 compact:h-11 compact:gap-3 compact:rounded-none compact:px-4",
                        entry.prefix && "compact:h-14",
                      )}
                    >
                      {/* On a phone every glyph sits in the same box, the size of a person's
                          Disc, so each group's labels start on one edge. */}
                      <span className="contents compact:flex compact:size-5 compact:shrink-0 compact:items-center compact:justify-center">
                        {entry.glyph}
                      </span>
                      {compact && entry.prefix ? (
                        // A phone's row has no width for four things side by side, so a ticket's
                        // title has the first line to itself, and its key and Project the second.
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate">{entry.label}</span>
                          <span className="truncate text-xs text-subtle-foreground">
                            {entry.prefix}
                            {entry.trailing && ` · ${entry.trailing}`}
                          </span>
                        </span>
                      ) : (
                        <>
                          {entry.prefix && (
                            <span className="min-w-14 shrink-0 text-xs text-subtle-foreground tabular-nums">
                              {entry.prefix}
                            </span>
                          )}
                          <span className="truncate">{entry.label}</span>
                          {entry.secondary && (
                            <span className="shrink-0 text-xs text-subtle-foreground">
                              {entry.secondary}
                            </span>
                          )}
                          {entry.trailing && (
                            <span className="ml-auto max-w-1/3 shrink-0 truncate pl-2 text-xs text-subtle-foreground">
                              {entry.trailing}
                            </span>
                          )}
                        </>
                      )}
                      {entry.shortcut && (
                        <span className="ml-auto">
                          <Kbd shortcut={entry.shortcut} />
                        </span>
                      )}
                    </ComboboxItem>
                  ))}
                </ComboboxGroup>
              )
            })}
          </ComboboxList>
          {/*
           * A status region, so a screen reader hears when a query leaves nothing. It stays
           * mounted to be heard, and takes no room until it has something to say. While the ticket
           * search still owes an answer, it says so, rather than that nothing matches.
           */}
          <ComboboxEmpty className="flex empty:p-0">
            {searching ? "Searching tickets…" : undefined}
          </ComboboxEmpty>
        </Combobox>
        <div className="flex h-8 items-center border-t border-border-subtle px-3 text-xs text-subtle-foreground compact:hidden">
          ↑↓ to navigate · ↵ to open · esc to close
        </div>
      </DialogContent>
    </Dialog>
  )
}
